/**
 * Mock API Configuration
 *
 * To add a new mock endpoint, simply add an entry to the routes array.
 * The system will automatically match requests and handle them accordingly.
 *
 * @example Adding a simple JSON file mock
 * ```typescript
 * {
 *   method: 'GET',
 *   path: '/my-new-endpoint',
 *   type: 'json-file',
 *   handler: '/assets/mocks/my-data.json'
 * }
 * ```
 *
 * @example Adding a custom handler
 * ```typescript
 * {
 *   method: 'POST',
 *   path: '/my-custom-endpoint',
 *   type: 'custom',
 *   handler: 'handleMyCustomEndpoint'
 * }
 * // Then add the method to MockService:
 * // private handleMyCustomEndpoint(req: HttpRequest<unknown>): MockResult { ... }
 * ```
 *
 * @example Using regex patterns
 * ```typescript
 * {
 *   method: 'GET',
 *   path: /^\/items\/\d+$/,
 *   type: 'custom',
 *   handler: 'handleGetItem'
 * }
 * ```
 */

export type MockHandlerType =
  | 'json-file' // Load from JSON file
  | 'custom' // Use custom handler function
  | 'state-dependent'; // Uses internal state (auth, user data, etc.)

export interface MockRoute {
  /** HTTP method or '*' for any method */
  method: string;

  /** Path pattern (exact match or regex) */
  path: string | RegExp;

  /** Handler type */
  type: MockHandlerType;

  /** For 'json-file': path to JSON file. For 'custom': handler function name */
  handler: string;

  /** Optional: Transform response before returning */
  transform?: (
    data: unknown,
    req: { params: Map<string, string>; body: Record<string, unknown> }
  ) => unknown;
}

/**
 * Mock Routes Configuration
 * Add new routes here - the system will automatically handle them.
 */
export const MOCK_ROUTES: MockRoute[] = [
  // ============ Authentication ============
  {
    method: 'POST',
    path: '/auth/login',
    type: 'state-dependent',
    handler: 'handleLogin',
  },
  {
    method: 'POST',
    path: '/auth/register',
    type: 'state-dependent',
    handler: 'handleRegister',
  },
  {
    method: 'POST',
    path: '/auth/logout',
    type: 'state-dependent',
    handler: 'handleLogout',
  },
  {
    method: 'POST',
    path: '/auth/refresh',
    type: 'state-dependent',
    handler: 'handleRefresh',
  },
  {
    method: 'GET',
    path: '/auth/me',
    type: 'state-dependent',
    handler: 'handleCurrentUser',
  },

  // Password Reset
  {
    method: 'POST',
    path: '/auth/reset-password',
    type: 'json-file',
    handler: '/assets/mocks/auth/password-reset-request.json',
  },
  {
    method: 'POST',
    path: '/auth/password-reset/request',
    type: 'json-file',
    handler: '/assets/mocks/auth/password-reset-request.json',
  },
  {
    method: 'POST',
    path: '/auth/verify-otp',
    type: 'json-file',
    handler: '/assets/mocks/auth/password-reset-verify.json',
  },
  {
    method: 'POST',
    path: '/auth/password-reset/verify',
    type: 'json-file',
    handler: '/assets/mocks/auth/password-reset-verify.json',
  },
  {
    method: 'POST',
    path: '/auth/update-password',
    type: 'json-file',
    handler: '/assets/mocks/auth/password-reset-reset.json',
  },
  {
    method: 'POST',
    path: '/auth/password-reset/reset',
    type: 'json-file',
    handler: '/assets/mocks/auth/password-reset-reset.json',
  },

  // ============ Bible ============
  {
    method: 'GET',
    path: '/bible/versions',
    type: 'custom',
    handler: 'handleVersions',
  },
  {
    method: 'GET',
    path: '/bible/passage',
    type: 'custom',
    handler: 'handlePassage',
  },
  {
    method: 'GET',
    path: '/bible/books',
    type: 'custom',
    handler: 'handleBibleBooks',
  },

  // ============ Audio Bible ============
  {
    method: 'GET',
    path: '/audio/bibles',
    type: 'custom',
    handler: 'handleAudioVersions',
  },
  {
    method: 'GET',
    path: '/audio/books',
    type: 'custom',
    handler: 'handleAudioBooks',
  },
  {
    method: 'GET',
    path: '/audio/chapters',
    type: 'custom',
    handler: 'handleAudioChapters',
  },
  {
    method: 'GET',
    path: '/audio/chapter',
    type: 'custom',
    handler: 'handleAudioChapter',
  },
  {
    method: 'GET',
    path: '/audio/preferences',
    type: 'state-dependent',
    handler: 'handleAudioPreferences',
  },
  {
    method: 'POST',
    path: '/audio/preferences',
    type: 'state-dependent',
    handler: 'handleSaveAudioPreferences',
  },
  {
    method: 'POST',
    path: '/audio/track',
    type: 'state-dependent',
    handler: 'handleAudioTrackPlayback',
  },

  // ============ Settings / Profile ============
  {
    method: 'GET',
    path: '/settings',
    type: 'state-dependent',
    handler: 'handleGetSettings',
  },
  {
    method: 'GET',
    path: '/settings/profile',
    type: 'state-dependent',
    handler: 'handleCurrentUser',
  },
  {
    method: 'PUT',
    path: '/settings/profile',
    type: 'state-dependent',
    handler: 'handleUpdateProfile',
  },
  {
    method: 'POST',
    path: '/settings/profile/avatar',
    type: 'state-dependent',
    handler: 'handleUploadProfileAvatar',
  },

  // ============ Themes ============
  {
    method: 'GET',
    path: '/passage-themes',
    type: 'custom',
    handler: 'handlePassageThemes',
  },
  {
    method: 'GET',
    path: /^\/themes\/\d+\/chapters$/,
    type: 'custom',
    handler: 'handleThemeChapters',
  },

  // ============ Passage Preferences ============
  {
    method: 'GET',
    path: '/passage/preferences',
    type: 'json-file',
    handler: '/assets/mocks/bible/preferences.json',
  },

  // ============ Dashboard ============
  {
    method: 'GET',
    path: '/dashboard/overview',
    type: 'json-file',
    handler: '/assets/mocks/dashboard/overview.json',
  },
  {
    method: 'GET',
    path: '/dashboard/stats',
    type: 'json-file',
    handler: '/assets/mocks/dashboard/stats.json',
  },
  {
    method: 'GET',
    path: '/dashboard/recent-passages',
    type: 'json-file',
    handler: '/assets/mocks/dashboard/recent-passages.json',
  },
  {
    method: 'GET',
    path: /^\/dashboard\/verse-of-the-day(\/[a-z]{3})?$/,
    type: 'json-file',
    handler: '/assets/mocks/dashboard/verse-of-the-day.json',
  },

  // ============ Dictionary ============
  {
    method: 'GET',
    path: '/dictionary/home',
    type: 'state-dependent',
    handler: 'handleDictionaryHome',
  },
  {
    method: 'GET',
    path: '/dictionary/entry',
    type: 'state-dependent',
    handler: 'handleDictionaryEntry',
  },

  // ============ Study Tracking ============
  {
    method: 'GET',
    path: '/study-tracking/resume',
    type: 'state-dependent',
    handler: 'handleDictionaryResume',
  },
  {
    method: 'POST',
    path: '/study-tracking/start',
    type: 'state-dependent',
    handler: 'handleStartStudyTracking',
  },
  {
    method: 'PUT',
    path: /^\/study-tracking\/.+$/,
    type: 'state-dependent',
    handler: 'handleUpdateStudyTracking',
  },
  {
    method: 'POST',
    path: /^\/study-tracking\/.+\/end$/,
    type: 'state-dependent',
    handler: 'handleEndStudyTracking',
  },

  // ============ Highlights ============
  {
    method: 'POST',
    path: '/highlights/save',
    type: 'state-dependent',
    handler: 'handleSaveHighlight',
  },

  // ============ Notes ============
  {
    method: 'POST',
    path: '/notes/save',
    type: 'state-dependent',
    handler: 'handleSaveNote',
  },
  {
    method: 'POST',
    path: '/notes',
    type: 'state-dependent',
    handler: 'handleSaveNote',
  },
  {
    method: 'DELETE',
    path: /^\/notes\/.+/,
    type: 'state-dependent',
    handler: 'handleDeleteNote',
  },
  {
    method: 'POST',
    path: '/notes/upload-recording',
    type: 'json-file',
    handler: '/assets/mocks/notes/upload-recording.json',
  },

  // ============ Reading Sessions ============
  {
    method: 'POST',
    path: '/user/reading-sessions/start',
    type: 'state-dependent',
    handler: 'handleStartReadingSession',
  },
  {
    method: 'PUT',
    path: /^\/user\/reading-sessions\/.+/,
    type: 'state-dependent',
    handler: 'handleUpdateReadingSession',
  },
  {
    method: 'POST',
    path: /^\/user\/reading-sessions\/.+\/end$/,
    type: 'state-dependent',
    handler: 'handleEndReadingSession',
  },
];
